import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useMemo,
	useRef,
	useState,
} from "react";
import type { Entry } from "./types";

export type UseEntryTableSelectionResult = {
	checkedRows: Set<number>;
	setCheckedRows: Dispatch<SetStateAction<Set<number>>>;
	selectAllChecked: boolean;
	selectAllIndeterminate: boolean;
	onToggleSelectAll: () => void;
	onToggleRow: (id: number, e: React.MouseEvent) => void;
};

/**
 * Multi-select state machine for EntriesTable rows.
 * Ported 1:1 from TrackedView.tsx (the inline implementation it used before this hook existed).
 *
 * Plain click toggles the clicked row's checkbox without clearing other selections.
 * Ctrl/Cmd+click toggles the clicked row's checkbox (same as plain click; preserved for API compatibility).
 * Shift+click replaces the selection with the range from the last anchor to the clicked id.
 * Ctrl+Shift+click adds the range from the last anchor to the clicked id.
 * Shift+click does not advance the anchor — this lets the user re-adjust the range end.
 */
export function useEntryTableSelection({
	entries,
	groupedEntries,
}: {
	entries: Entry[];
	groupedEntries: Record<string, Entry[]>;
}): UseEntryTableSelectionResult {
	const [checkedRows, setCheckedRows] = useState<Set<number>>(new Set());
	const lastClickedId = useRef<number | null>(null);

	const count = entries.length;
	const selectAllChecked = checkedRows.size > 0 && checkedRows.size === count;
	const selectAllIndeterminate =
		checkedRows.size > 0 && checkedRows.size < count;

	const flatEntryIds = useMemo(() => {
		const ids: number[] = [];
		for (const key of Object.keys(groupedEntries)) {
			for (const entry of groupedEntries[key]) ids.push(entry.id);
		}
		return ids;
	}, [groupedEntries]);

	const onToggleSelectAll = useCallback(() => {
		if (selectAllChecked || selectAllIndeterminate) {
			setCheckedRows(new Set());
		} else {
			setCheckedRows(new Set(entries.map((e) => e.id)));
		}
	}, [selectAllChecked, selectAllIndeterminate, entries]);

	const onToggleRow = useCallback(
		(id: number, e: React.MouseEvent) => {
			const hasCtrl = e.ctrlKey || e.metaKey;

			if (e.shiftKey && lastClickedId.current !== null) {
				const from = flatEntryIds.indexOf(lastClickedId.current);
				const to = flatEntryIds.indexOf(id);
				if (from !== -1 && to !== -1) {
					const start = Math.min(from, to);
					const end = Math.max(from, to);
					const rangeIds = flatEntryIds.slice(start, end + 1);
					if (hasCtrl) {
						setCheckedRows((prev) => {
							const next = new Set(prev);
							for (const rid of rangeIds) next.add(rid);
							return next;
						});
					} else {
						setCheckedRows(new Set(rangeIds));
					}
					return;
				}
			}

			setCheckedRows((prev) => {
				const next = new Set(prev);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
			lastClickedId.current = id;
		},
		[flatEntryIds],
	);

	return {
		checkedRows,
		setCheckedRows,
		selectAllChecked,
		selectAllIndeterminate,
		onToggleSelectAll,
		onToggleRow,
	};
}
